<p-dialog
  header="Import from DrillPlan"
  [visible]="visible"
  (visibleChange)="onVisibleChange($event)"
  [modal]="true"
  [style]="{ width: '560px' }"
  [closable]="!isImporting()">



  @if (!importResult()) {
    @if (isCheckingConnection()) {
      <div class="flex items-center gap-2 py-4">
        <i class="pi pi-spin pi-spinner"></i>
        <span>Checking connection...</span>
      </div>
    }

    @if (!isCheckingConnection() && !isConnected()) {
      <div class="flex flex-col items-center gap-4 py-6">
        <i class="pi pi-link text-5xl text-primary"></i>
        <p class="text-center text-gray-600 dark:text-gray-300">
          Connect to SLB DrillPlan to import well data.
        </p>
        <p-button
          class="outlined-button"
          severity="success"
          outlined="true"
          size="large"
          label="Connect to DrillPlan"
          icon="pi pi-external-link"
          [loading]="isConnecting()"
          (onClick)="connect()">
        </p-button>
      </div>
    }

    @if (!isCheckingConnection() && isConnected()) {
      <div class="flex flex-col gap-4 py-2">

        <p-message severity="info" styleClass="w-full">
          <span class="text-sm">ℹ️ Make sure you have created and switched to the PERIFOR design you want to populate before importing. <br>⚠️ Import will overwrite data for each selected category in the active design.</span>
        </p-message>

        @if (isImporting()) {
          <p-message severity="info" styleClass="w-full">
            <div class="flex flex-col gap-1 text-sm">
              <span class="font-medium"><i class="pi pi-spin pi-spinner mr-2"></i>Import in progress</span>
              <span>{{ importStatusMessage() }}</span>
              <span class="text-xs opacity-90">Elapsed: {{ importElapsedDisplay() }}</span>
            </div>
          </p-message>
        }


        <div class="flex flex-col gap-1">
          <label class="font-medium">Project</label>
          <p-select
            [options]="projects()"
            [ngModel]="selectedProjectId()"
            (onChange)="onProjectChange($event.value)"
            optionLabel="wellName"
            optionValue="projectId"
            placeholder="Select a project"
            [filter]="true"
            filterBy="wellName"
            [disabled]="isLoadingProjects()"
            appendTo="body"
            class="w-full">
          </p-select>
        </div>

        <div class="flex flex-col gap-1">
          <label class="font-medium">Plan</label>
          <p-select
            [options]="plans()"
            [ngModel]="selectedPlanId()"
            (onChange)="selectedPlanId.set($event.value)"
            optionLabel="name"
            optionValue="planId"
            placeholder="Select a plan"
            [disabled]="!selectedProjectId() || isLoadingPlans()"
            appendTo="body"
            class="w-full">
          </p-select>
        </div>

        <div class="flex flex-col gap-2">
          <label class="font-medium">Optional Import Categories</label>
          <div class="flex flex-col gap-2 pl-1">
            <div class="flex flex-col gap-0.5 pl-0.5">
              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="importTrajectory"
                  [ngModel]="importTrajectory()"
                  (ngModelChange)="importTrajectory.set($event)"
                  [binary]="true">
                </p-checkbox>
                <label for="importTrajectory">Trajectory</label>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-300 pl-7">{{ importOptionDetails.trajectory }}</div>
            </div>
            <div class="flex flex-col gap-0.5 pl-0.5">
              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="importWellboreGeometry"
                  [ngModel]="importWellboreGeometry()"
                  (ngModelChange)="importWellboreGeometry.set($event)"
                  [binary]="true">
                </p-checkbox>
                <label for="importWellboreGeometry">Well Configuration (Tubulars/Geometry)</label>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-300 pl-7">{{ importOptionDetails.wellConfiguration }}</div>
            </div>
            <div class="flex flex-col gap-0.5 pl-0.5">
              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="importFormationMarkers"
                  [ngModel]="importFormationMarkers()"
                  (ngModelChange)="importFormationMarkers.set($event)"
                  [binary]="true">
                </p-checkbox>
                <label for="importFormationMarkers">Formation Markers</label>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-300 pl-7">{{ importOptionDetails.formationMarkers }}</div>
            </div>
            <div class="flex flex-col gap-0.5 pl-0.5">
              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="importPressureWindow"
                  [ngModel]="importPressureWindow()"
                  (ngModelChange)="importPressureWindow.set($event)"
                  [binary]="true">
                </p-checkbox>
                <label for="importPressureWindow">Pressure Window (PPFG)</label>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-300 pl-7">{{ importOptionDetails.pressureWindow }}</div>
            </div>
            <div class="flex flex-col gap-0.5 pl-0.5">
              <div class="flex items-center gap-2">
                <p-checkbox
                  inputId="importTemperature"
                  [ngModel]="importTemperature()"
                  (ngModelChange)="importTemperature.set($event)"
                  [binary]="true">
                </p-checkbox>
                <label for="importTemperature">Temperature Gradient</label>
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-300 pl-7">{{ importOptionDetails.temperature }}</div>
            </div>
          </div>
          <div class="text-xs text-gray-600 dark:text-gray-300 pl-1">
            <div class="font-medium">Always imported</div>
            <ul class="list-disc pl-4 mt-1">
              @for (item of alwaysImportedItems; track item) {
                <li>{{ item }}</li>
              }
            </ul>
          </div>

        </div>

            </div>
          }
        }

  @if (importResult(); as result) {
    <div class="flex flex-col gap-3 py-2">
      <p-message severity="success" styleClass="w-full">
        <span class="text-sm">{{ result.summary }}</span>
      </p-message>

      @if (result.report; as report) {
        @if (report.applied.length > 0) {
          <div class="flex flex-col gap-1">
            <div class="font-medium">✅ Applied</div>
            @for (item of report.applied; track item.category + item.message) {
              <div class="text-sm">[{{ item.category }}] {{ item.message }}</div>
            }
          </div>
        }

        @if (report.reviewRequired.length > 0) {
          <div class="flex flex-col gap-1">
            <div class="font-medium">⚠️ Review recommended</div>
            @for (item of report.reviewRequired; track item.category + item.message) {
              <div class="text-sm">[{{ item.category }}] {{ item.message }}</div>
            }
          </div>
        }

        @if (report.actionRequired.length > 0) {
          <div class="flex flex-col gap-1">
            <div class="font-medium">✏️ Action required</div>
            @for (item of report.actionRequired; track item.category + item.message) {
              <div class="text-sm">[{{ item.category }}] {{ item.message }}</div>
            }
          </div>
        }

        @if (report.notImported.length > 0) {
          <div class="flex flex-col gap-1">
            <div class="font-medium">ℹ️ Not imported</div>
            @for (item of report.notImported; track item.category + item.message) {
              <div class="text-sm">[{{ item.category }}] {{ item.message }}</div>
            }
          </div>
        }
      }
    </div>
  }

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      @if (importResult()) {
        <p-button
          label="Copy report"
          icon="pi pi-copy"
          severity="secondary"
          [outlined]="true"
          (onClick)="copyReport()">
        </p-button>
        <p-button
          label="Close"
          icon="pi pi-check"
          (onClick)="close()">
        </p-button>
      } @else {
        @if (isConnected() && isDevEnvironment) {
          <p-button
            label="Download Artifacts"
            icon="pi pi-folder-open"
            severity="secondary"
            [outlined]="true"
            [disabled]="!selectedPlanId()"
            [loading]="isDownloadingArtifacts()"
            pTooltip="Dev tool: saves all latest artifact XMLs to %TEMP%\DrillPlanArtifacts\"
            tooltipPosition="top"
            (onClick)="downloadArtifacts()">
          </p-button>
        }
        @if (isConnected()) {
          <p-button
            label="Import"
            icon="pi pi-download"
            [disabled]="!canImport() || isImporting()"
            [loading]="isImporting()"
            (onClick)="import()">
          </p-button>
        }
        <p-button
          label="Cancel"
          icon="pi pi-times"
          severity="danger"
          [outlined]="true"
          (onClick)="close()"
          [disabled]="isImporting()">
        </p-button>
      }
    </div>
  </ng-template>
</p-dialog>
